/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, ForkEffect, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { gameLogger, selectGameId, selectUserId } from '~/app';
import {
  SendAddBonusCards,
  sendAddBonusCards,
  SendLeaveGame,
  sendLeaveGame,
  SendMergeCards,
  sendMergeCards,
  SendMessage,
  sendMessage,
  SendSelectCardDice,
  sendSelectCardDice,
  SendSelectDice,
  sendSelectDice,
  SendSelectMove,
  sendSelectMove,
} from '~/overlay';
import { Controller } from '~/server/src/interfaces';
import Turfmaster from '~/server/src/tmmodel';

import { leaveAcked, leaveFailed, leaveRequested, leaveRetrying } from './actions';

function* sagaGameSender(controller: Controller): Generator<ForkEffect<never>, void, unknown> {
  yield takeEvery(
    (action: { type: string }) => /^send/.test(action.type),
    function* (action: PayloadAction<unknown>) {
      // Get userId from Redux state and add it to all player messages
      const userId: string | null = yield select(selectUserId);
      const gameId: string | null = yield select(selectGameId);

      switch (action.type) {
        case sendSelectCardDice.type: {
          const payloadSelectCardDice = action.payload as SendSelectCardDice;
          controller
            .receiveMsg(Turfmaster.stateMachine.SELECTCARDDICE, {
              ...payloadSelectCardDice,
              userid: userId,
            })
            .catch((err) => {
              gameLogger.error(
                { err, action: 'SELECTCARDDICE', payload: payloadSelectCardDice },
                'controller.receiveMsg failed',
              );
            });
          break;
        }
        case sendSelectMove.type: {
          const payloadSelectMove = action.payload as SendSelectMove;
          controller
            .receiveMsg(Turfmaster.stateMachine.SELECTMOVE, {
              ...payloadSelectMove,
              userid: userId,
            })
            .catch((err) => {
              gameLogger.error(
                { err, action: 'SELECTMOVE', payload: payloadSelectMove },
                'controller.receiveMsg failed',
              );
            });
          break;
        }
        case sendSelectDice.type: {
          const payloadSelectDice = action.payload as SendSelectDice;
          controller
            .receiveMsg(Turfmaster.stateMachine.SELECTDICES, {
              ...payloadSelectDice,
              userid: userId,
            })
            .catch((err) => {
              gameLogger.error(
                { err, action: 'SELECTDICES', payload: payloadSelectDice },
                'controller.receiveMsg failed',
              );
            });
          break;
        }
        case sendMergeCards.type: {
          const payloadMergeCards = action.payload as SendMergeCards;

          // make sure that the sum of the new card deck is exactly 10 cards
          // tbd: bonus cards can lead to a different allowed sum
          if (10 + payloadMergeCards.added.length - payloadMergeCards.removed.length === 10)
            controller
              .receiveMsg(Turfmaster.stateMachine.MERGECARDS, {
                ...payloadMergeCards,
                userid: userId,
              })
              .catch((err) => {
                gameLogger.error(
                  { err, action: 'MERGECARDS', payload: payloadMergeCards },
                  'controller.receiveMsg failed',
                );
              });
          break;
        }
        case sendAddBonusCards.type: {
          const payloadAddBonusCards = action.payload as SendAddBonusCards;
          controller
            .receiveMsg(Turfmaster.stateMachine.ADDBONUSCARDS, {
              ...payloadAddBonusCards,
              userid: userId,
            })
            .catch((err) => {
              gameLogger.error(
                { err, action: 'ADDBONUSCARDS', payload: payloadAddBonusCards },
                'controller.receiveMsg failed',
              );
            });
          break;
        }
        case sendMessage.type: {
          const payloadMessage = action.payload as SendMessage;
          controller
            .receiveMsg(Turfmaster.stateMachine.SENDMESSAGE, {
              message: payloadMessage.message,
              userid: userId,
              gameId: gameId ?? undefined,
            })
            .catch((err) => {
              gameLogger.error(
                { err, action: 'SENDMESSAGE', payload: payloadMessage },
                'controller.receiveMsg failed',
              );
            });
          break;
        }
        case sendLeaveGame.type: {
          const payloadLeaveGame = action.payload as SendLeaveGame;
          const leaveState =
            Turfmaster.stateMachine['LEAVEGAME' as keyof typeof Turfmaster.stateMachine];
          const payloadWithUser = { ...payloadLeaveGame, userid: userId };

          const maxAttempts = 3;
          const ackTimeoutMs = 4000;

          // Always surface leave progress to UI to avoid board lock-in on silent failures.
          yield put(leaveRequested({ attempt: 1 }));

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            if (attempt > 1) {
              yield put(leaveRetrying({ attempt }));
              // Small linear backoff; keep it snappy so the user sees quick progress.
              yield delay(250 * (attempt - 1));
            }

            try {
              yield call([controller, controller.receiveMsg], leaveState, payloadWithUser);
            } catch (err) {
              const isAuthLikely = userId == null;
              const message = err instanceof Error ? err.message : 'unknown_error';
              gameLogger.error(
                { err, action: 'LEAVEGAME', payload: payloadLeaveGame, attempt, userId },
                'controller.receiveMsg failed',
              );

              if (attempt === maxAttempts)
                yield put(leaveFailed({ reason: isAuthLikely ? 'auth' : 'controller', message }));
              continue;
            }

            const { ack, fail } = yield race({
              ack: take(leaveAcked.type),
              fail: take(leaveFailed.type),
              timeout: delay(ackTimeoutMs),
            });
            if (fail) {
              // Remote server responded with a definitive error (leavegameerror mapped in receiver).
              // Stop retrying immediately so we don't degrade into an ack timeout.
              break;
            }
            if (ack) {
              break;
            }

            gameLogger.warn(
              { action: 'LEAVEGAME', attempt, ackTimeoutMs, userId },
              'leavegame ack timeout',
            );
            if (attempt === maxAttempts) {
              yield put(
                leaveFailed({
                  reason: userId == null ? 'auth' : 'network',
                  message: 'leave_ack_timeout',
                }),
              );
            }
          }
          break;
        }
      }
    },
  );
}

export default sagaGameSender;
